import React, { useState } from 'react'
import { recordFeedback } from '../api'
import Badge from './Badge'

export function urgencyBadge(urgency, daysLeft) {
  if (urgency === 'urgent') return <Badge variant="urgent">🔴 {daysLeft}d left</Badge>
  if (urgency === 'soon')   return <Badge variant="soon">🟡 {daysLeft}d left</Badge>
  if (urgency === 'open')   return <Badge variant="open">🟢 Open</Badge>
  if (urgency === 'past')   return <Badge variant="urgent">Closed</Badge>
  return <Badge>No deadline</Badge>
}

export function awardFormat(floor, ceiling) {
  const fmt = cents => {
    const d = Math.round(cents / 100)
    if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`
    if (d >= 1_000)     return `$${d.toLocaleString()}`
    return `$${d}`
  }
  if (floor && ceiling) return `${fmt(floor)} – ${fmt(ceiling)}`
  if (ceiling) return `Up to ${fmt(ceiling)}`
  if (floor)   return `From ${fmt(floor)}`
  return null
}

/**
 * Compact match row (shared by the dashboard and the grant-search page).
 * Single line of title, a meta line (agency · award · due), the score,
 * urgency badge, and inline feedback — expandable to reveal the why-matched
 * reason + actions on demand, so a long list stays scannable.
 *
 * Feedback thumbs render only when the grant is one of the client's real
 * matches (grant.has_match !== false) — POST /api/feedback requires a
 * matches row, and search results from the whole corpus may not have one.
 */
export default function GrantCard({ grant, clientId, onFeedback }) {
  const [feedbackSent, setFeedbackSent] = useState(grant.feedback || null)
  const [sending, setSending] = useState(false)
  const [open, setOpen] = useState(false)

  const canFeedback = grant.has_match !== false

  async function handleFeedback(value) {
    if (sending) return
    setSending(true)
    try {
      await recordFeedback({ client_id: clientId, grant_id: grant.grant_id, feedback: value })
      setFeedbackSent(value)
      onFeedback?.(grant.grant_id, value)
    } catch (_) {}
    setSending(false)
  }

  const award = awardFormat(grant.award_floor, grant.award_ceiling)

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all">
      {/* Compact header row — click to expand */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <div className="flex-shrink-0 w-10 text-center">
          <div className="text-lg font-bold text-brand-500 leading-none">{grant.score}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">score</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 text-sm leading-snug truncate">{grant.title}</p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {grant.agency_name}
            {award && <> · {award}</>}
            {grant.close_date && <> · Due {grant.close_date}</>}
          </p>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2">
          {urgencyBadge(grant.urgency, grant.days_left)}
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20" fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="px-4 pb-3 pt-0 space-y-3 border-t border-gray-100">
          {grant.why_matched && (
            <p className="text-sm text-gray-600 italic border-l-2 border-brand-100 pl-3 pt-3">
              {grant.why_matched}
            </p>
          )}
          <div className="flex items-center gap-3">
            <a
              href={grant.opportunity_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-brand-500 hover:underline"
            >
              View opportunity →
            </a>
            <div className="flex-1" />
            {canFeedback && (feedbackSent ? (
              <span className="text-xs text-gray-500">
                {feedbackSent === 'relevant' ? '👍 Marked relevant' : '👎 Marked not for us'}
              </span>
            ) : (
              <>
                <button
                  onClick={() => handleFeedback('relevant')}
                  disabled={sending}
                  className="text-sm text-gray-500 hover:text-green-600 disabled:opacity-50 transition-colors"
                  title="Relevant"
                >👍</button>
                <button
                  onClick={() => handleFeedback('irrelevant')}
                  disabled={sending}
                  className="text-sm text-gray-500 hover:text-red-500 disabled:opacity-50 transition-colors"
                  title="Not for us"
                >👎</button>
              </>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
